import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
    useDeleteEnvironmentMutation,
    useStartEnvironmentMutation,
    useStopEnvironmentMutation,
    useUpdateInstanceMutation,
    useFetchServerInstanceMutation,
    useGetInstancesQuery
} from '../store/services/cloudlab/cloudlab';
import { showNotification } from '../store/slices/NotificationSystemSlice';

export const useInstanceManagement = () => {
    const dispatch = useDispatch();
    const [transitionalInstances, setTransitionalInstances] = useState(new Set());
    const { data: instancesData, isLoading, error, refetch: fetchInstances } = useGetInstancesQuery();
    const [deleteEnvironment] = useDeleteEnvironmentMutation();
    const [startEnvironment] = useStartEnvironmentMutation();
    const [stopEnvironment] = useStopEnvironmentMutation();
    const [fetchServerInstance] = useFetchServerInstanceMutation();
    const [updateInstance] = useUpdateInstanceMutation();

    const instances = instancesData?.data || [];

    // Time ago helper
    const timeAgo = useCallback((timestamp) => {
        const createdAt = new Date(timestamp);
        const now = new Date();
        const diffInMs = now - createdAt;
        const diffInMins = Math.floor(diffInMs / 60000);
        const diffInHours = Math.floor(diffInMins / 60);
        const diffInDays = Math.floor(diffInHours / 24);

        if (diffInMins < 1) return 'Just now';
        if (diffInMins === 1) return '1 minute ago';
        if (diffInMins < 60) return `${diffInMins} minutes ago`;
        if (diffInHours === 1) return '1 hour ago';
        if (diffInHours < 24) return `${diffInHours} hours ago`;
        if (diffInDays === 1) return '1 day ago';
        return `${diffInDays} days ago`;
    }, []);

    // Update environment
    const UpdateEnvironment = async (envName) => {
        try {
            const response = await updateInstance(envName).unwrap();
            return response;
        } catch (error) {
            throw error;
        }
    };

    // Refresh environment
    const refreshEnvironment = async (envName) => {
        try {
            const resp = await UpdateEnvironment(envName);
            fetchInstances();
            return { message: 'success', data: resp };
        } catch (error) {
            return { message: 'error', error };
        }
    };

    // Polling for status
    const pollEnvironmentStatus = async (envName, maxRetries = 3, interval = 30000) => {
        let attempts = 0;
        while (attempts < maxRetries) {
            attempts++;
            try {
                const response = await updateInstance(envName).unwrap();
                const status = response.data?.status;
                if (status === 'STARTED' || status === 'STOPPED') {
                    return status;
                }
                await new Promise(res => setTimeout(res, interval));
            } catch (error) {
                break;
            }
        }
        return null;
    };

    // Delete env
    const handleDeleteEnv = async (envId, templateName) => {
        try {
            await deleteEnvironment(envId).unwrap();
            fetchInstances();
            setCreatedEnvs(prev => prev.filter(name => name !== templateName)); // <-- Remove from createdEnvs
            dispatch(showNotification({
                message: 'Environment deleted successfully!',
                success: true,
                duration: 3000,
                position: 'top-center',
            }));
        } catch (error) {
            dispatch(showNotification({
                message: 'An error occurred while deleting the environment.',
                success: false,
                duration: 3000,
                position: 'top-center',
            }));
        }
    };

    // Resume env
    const handleResume = async (envId) => {
        try {
            await startEnvironment(envId).unwrap();
            dispatch(showNotification({
                message: 'Environment is resuming...',
                success: true,
                duration: 3000,
                position: 'top-center',
            }));
            await pollEnvironmentStatus(envId);
            fetchInstances();
        } catch (error) {
            if (error.response?.status === 402) {
                const { requiredCredits, currentCredits } = error.response.data;
                const displayCredits = currentCredits < 0 ? 0 : currentCredits;
                dispatch(showNotification({
                    message: `Need ${requiredCredits} credits (you have ${displayCredits}). Add more to continue.`,
                    success: false,
                    duration: 4000,
                    position: 'top-center',
                }));
            } else {
                dispatch(showNotification({
                    message: 'Failed to resume environment.',
                    success: false,
                    duration: 3000,
                    position: 'top-center',
                }));
            }
        }
    };

    // Pause env
    const handlePause = async (envId) => {
        try {
            await stopEnvironment(envId).unwrap();
            dispatch(showNotification({
                message: 'Environment is stopping...',
                success: true,
                duration: 3000,
                position: 'top-center',
            }));
            await fetchServerInstance(envId).unwrap();
            fetchInstances();
        } catch (error) {
            dispatch(showNotification({
                message: 'Failed to stop environment.',
                success: false,
                duration: 3000,
                position: 'top-center',
            }));
        }
    };

    // Start env (open playground or show error)
    const handleStart = async (envId) => {
        try {
            const response = await updateInstance(envId).unwrap();
            const envData = response.data;
            if (!envData) {
                dispatch(showNotification({
                    message: 'No environment data available.',
                    success: false,
                    duration: 3000,
                    position: 'top-center',
                }));
                return null;
            }
            const instanceExports = envData.envExports;
            if (!instanceExports) {
                dispatch(showNotification({
                    message: 'Instance data not available.',
                    success: false,
                    duration: 3000,
                    position: 'top-center',
                }));
                return null;
            }
            // Return the envExports to the caller
            return instanceExports;
        } catch (error) {
            dispatch(showNotification({
                message: 'Failed to start environment.',
                success: false,
                duration: 3000,
                position: 'top-center',
            }));
            return null;
        }
    };

    // Transitional polling
    const updateTransitionalInstances = useCallback(async () => {
        try {
            const currentInstances = [...instances];
            const instancesWithStatus = currentInstances.filter(instance => instance.envStatus);
            if (instancesWithStatus.length === 0) {
                setTransitionalInstances(new Set());
                return;
            }
            const updatePromises = instancesWithStatus.map(async instance => {
                try {
                    const response = await updateInstance(instance.envName).unwrap();
                    const currentStatus = instance.envStatus;
                    const updatedData = response?.data;
                    const updatedStatus = updatedData?.status;
                    const isTransitional = updatedStatus !== 'STARTED' &&
                        updatedStatus !== 'STOPPED' &&
                        updatedStatus !== 'FAILED';
                    if (!isTransitional) {
                        setTransitionalInstances(prev => {
                            const next = new Set(prev);
                            next.delete(instance.envName);
                            return next;
                        });
                    } else {
                        setTransitionalInstances(prev => {
                            const next = new Set(prev);
                            next.add(instance.envName);
                            return next;
                        });
                    }
                    if (updatedStatus &&
                        (updatedStatus !== currentStatus ||
                            JSON.stringify(updatedData?.envExports) !== JSON.stringify(instance.envExports))) {
                        return {
                            envName: instance.envName,
                            changes: {
                                envStatus: updatedStatus,
                                envExports: updatedData?.envExports,
                                templateName: updatedData?.templateName,
                            }
                        };
                    }
                    return null;
                } catch (error) {
                    return null;
                }
            });
            const updates = (await Promise.all(updatePromises)).filter(Boolean);
            if (updates.length > 0) {
                fetchInstances();
            }
        } catch (error) {
        }
    }, [instances, updateInstance, fetchInstances]);

    // Main polling effect
    useEffect(() => {
        const mainInterval = setInterval(() => {
            fetchInstances();
        }, 300000);
        const transitionInterval = setInterval(() => {
            if (transitionalInstances.size > 0) {
                updateTransitionalInstances();
            }
        }, 60000);
        return () => {
            clearInterval(mainInterval);
            clearInterval(transitionInterval);
        };
    }, [transitionalInstances, fetchInstances, updateTransitionalInstances]);

    // Set transitional instances on data change
    useEffect(() => {
        if (instances.length > 0) {
            const transitional = new Set(
                instances.filter(i => i.envStatus !== 'STARTED' && i.envStatus !== 'STOPPED' && i.envStatus !== 'FAILED')
                    .map(i => i.envName)
            );
            setTransitionalInstances(transitional);
        }
    }, [instances]);

    return {
        instances,
        isLoading,
        error,
        timeAgo,
        handleDeleteEnv,
        refreshEnvironment,
        handleResume,
        handlePause,
        handleStart,
    };
}; 